// شیدرهای پختنِ نور (GLSL ES 3.0 — WebGL2؛ روی سافاریِ آیفون و کرومِ اندروید هر دو هست)

export const MAX_LIGHTS = 16;

// ---------------------------------------------------------------------------------------------------
// ۱) «نقشه‌ی موقعیت/جهت»: هر مش با UVِ دومش (به‌جای دوربین) روی تصویرِ نقشه‌ی نور کشیده می‌شود و برای هر نقطه‌ی
//    تصویر، موقعیت و جهتِ واقعیِ سطح در دنیا ذخیره می‌شود. بعداً پرتوها از همین نقطه‌ها شلیک می‌شوند.

export const GBUFFER_VERT = /* glsl */ `
precision highp float;
in vec3 position;
in vec3 normal;
in vec2 uv1;
uniform mat4 modelMatrix;
uniform vec2 uJitter;
out vec3 vPos;
out vec3 vNrm;
void main() {
  vPos = (modelMatrix * vec4(position, 1.0)).xyz;
  vNrm = mat3(modelMatrix) * normal; // فقط برای «کدام طرف»؛ جهتِ دقیق در شیدرِ بعدی
  gl_Position = vec4(uv1 * 2.0 - 1.0 + uJitter, 0.0, 1.0);
}
`;

export const GBUFFER_FRAG = /* glsl */ `
precision highp float;
in vec3 vPos;
in vec3 vNrm;
uniform int uMode; // 0 = موقعیت، 1 = جهت
out vec4 fragColor;
void main() {
  if (uMode == 0) {
    fragColor = vec4(vPos, 1.0);
    return;
  }
  // جهتِ دقیقِ صفحه‌ی مثلث (نه جهتِ نرم‌شده‌ی رأس‌ها) — با همان هندسه‌ای که پرتوها به آن می‌خورند یکی است
  vec3 fn = cross(dFdx(vPos), dFdy(vPos));
  float len = length(fn);
  vec3 n = len > 1e-12 ? fn / len : normalize(vNrm);
  if (dot(n, vNrm) < 0.0) n = -n;
  fragColor = vec4(n, 1.0);
}
`;

// ---------------------------------------------------------------------------------------------------
// ۲) ردیابیِ مسیرِ نور (path tracing) برای هر نقطه‌ی نقشه‌ی نور: نورِ مستقیمِ خورشید و چراغ‌های سقفی (با پرتوِ
//    سایه) + نورِ غیرمستقیم (چند بار برخورد به دیوار/کف/سقف، و آسمان از پنجره). حاصل = «تابش» (irradiance) —
//    رنگِ نهاییِ هر سطح = رنگِ جنس × تابش / π (همان چیزی که MeshBasicMaterial با lightMap حساب می‌کند).
//    هر اجرا یک نمونه‌ی تازه به میانگینِ قبلی اضافه می‌کند (کانالِ آلفا = تعدادِ نمونه‌ها).

export const FULLSCREEN_VERT = /* glsl */ `
precision highp float;
in vec3 position;
void main() {
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const COMMON = /* glsl */ `
precision highp float;
precision highp int;
precision highp sampler2D;
#define PI 3.14159265359
#define INV_PI 0.31830988618
ivec2 tc(int i) { return ivec2(i % DATA_W, i / DATA_W); }
`;

export const TRACE_FRAG = /* glsl */ `
${COMMON}
#define STACK 40
#define LEAF 4
#define EPS 0.002

uniform sampler2D uPrev;
uniform sampler2D uPos;
uniform sampler2D uNrm;
uniform sampler2D uNodes;
uniform sampler2D uTris;
uniform sampler2D uMats;
uniform float uFrame;
uniform int uBounces;
uniform float uClamp;
uniform vec3 uSunDir;
uniform vec3 uSunE;
uniform float uSunCos;
uniform vec3 uSkyZenith;
uniform vec3 uSkyHorizon;
uniform vec3 uGround;
uniform int uLightCount;
uniform vec4 uLightRect[MAX_LIGHTS]; // xmin zmin xmax zmax
uniform vec4 uLightInfo[MAX_LIGHTS]; // y, cdf, area, احتمالِ انتخاب
uniform vec3 uLightL[MAX_LIGHTS];
out vec4 fragColor;

uint pcg(inout uint s) {
  s = s * 747796405u + 2891336453u;
  uint w = ((s >> ((s >> 28u) + 4u)) ^ s) * 277803737u;
  return (w >> 22u) ^ w;
}
float rnd(inout uint s) { return float(pcg(s)) * (1.0 / 4294967296.0); }

bool boxHit(vec3 mn, vec3 mx, vec3 ro, vec3 inv, float tmax) {
  vec3 t0 = (mn - ro) * inv;
  vec3 t1 = (mx - ro) * inv;
  vec3 a = min(t0, t1);
  vec3 b = max(t0, t1);
  float tn = max(max(a.x, a.y), max(a.z, 0.0));
  float tf = min(min(b.x, b.y), min(b.z, tmax));
  return tn <= tf;
}

float triHit(vec3 ro, vec3 rd, vec3 v0, vec3 v1, vec3 v2) {
  vec3 e1 = v1 - v0;
  vec3 e2 = v2 - v0;
  vec3 p = cross(rd, e2);
  float det = dot(e1, p);
  if (abs(det) < 1e-14) return -1.0;
  float inv = 1.0 / det;
  vec3 s = ro - v0;
  float u = dot(s, p) * inv;
  if (u < 0.0 || u > 1.0) return -1.0;
  vec3 q = cross(s, e1);
  float v = dot(rd, q) * inv;
  if (v < 0.0 || u + v > 1.0) return -1.0;
  return dot(e2, q) * inv;
}

// نزدیک‌ترین برخورد (anyHit = برای پرتوِ سایه: اولین برخورد کافی است)
float traverse(vec3 ro, vec3 rd, float tmax, bool anyHit, out int hitTri) {
  vec3 d = vec3(abs(rd.x) > 1e-9 ? rd.x : 1e-9, abs(rd.y) > 1e-9 ? rd.y : 1e-9, abs(rd.z) > 1e-9 ? rd.z : 1e-9);
  vec3 inv = 1.0 / d;
  hitTri = -1;
  float best = tmax;
  int stack[STACK];
  int sp = 0;
  stack[sp++] = 0;
  while (sp > 0) {
    int node = stack[--sp];
    vec4 a = texelFetch(uNodes, tc(node * 2), 0);
    vec4 b = texelFetch(uNodes, tc(node * 2 + 1), 0);
    if (!boxHit(a.xyz, b.xyz, ro, inv, best)) continue;
    if (b.w < 0.0) {
      if (sp < STACK - 1) {
        stack[sp++] = int(a.w);
        stack[sp++] = node + 1;
      }
    } else {
      int first = int(a.w);
      int cnt = int(b.w);
      for (int i = 0; i < LEAF; i++) {
        if (i >= cnt) break;
        int t = first + i;
        vec3 v0 = texelFetch(uTris, tc(t * 3), 0).xyz;
        vec3 v1 = texelFetch(uTris, tc(t * 3 + 1), 0).xyz;
        vec3 v2 = texelFetch(uTris, tc(t * 3 + 2), 0).xyz;
        float h = triHit(ro, rd, v0, v1, v2);
        if (h > 1e-5 && h < best) {
          best = h;
          hitTri = t;
          if (anyHit) return best;
        }
      }
    }
  }
  return best;
}

vec3 sky(vec3 d) {
  if (d.y < 0.0) return uGround;
  return mix(uSkyHorizon, uSkyZenith, sqrt(clamp(d.y, 0.0, 1.0)));
}

vec3 basisSample(vec3 n, float ct, float phi) {
  vec3 t = abs(n.y) < 0.999 ? normalize(cross(n, vec3(0.0, 1.0, 0.0))) : normalize(cross(n, vec3(1.0, 0.0, 0.0)));
  vec3 b = cross(n, t);
  float st = sqrt(max(0.0, 1.0 - ct * ct));
  return normalize(t * cos(phi) * st + b * sin(phi) * st + n * ct);
}

vec3 cosineSample(vec3 n, inout uint s) {
  float r2 = rnd(s);
  return basisSample(n, sqrt(max(0.0, 1.0 - r2)), 2.0 * PI * rnd(s));
}

// نورِ مستقیم (تابش) در نقطه‌ی p با جهتِ n
vec3 directE(vec3 p, vec3 n, inout uint s) {
  vec3 E = vec3(0.0);
  // خورشید — قرصِ کوچک (سایه‌ی لبه‌نرم)
  vec3 sd = basisSample(uSunDir, mix(1.0, uSunCos, rnd(s)), 2.0 * PI * rnd(s));
  float c = dot(n, sd);
  if (c > 0.0) {
    int h;
    traverse(p, sd, 1e5, true, h);
    if (h < 0) E += uSunE * c;
  }
  // یک چراغِ سقفی (به نسبتِ توان انتخاب می‌شود)
  if (uLightCount > 0) {
    float r = rnd(s);
    int li = 0;
    for (int i = 0; i < MAX_LIGHTS; i++) {
      if (i >= uLightCount) break;
      li = i;
      if (r <= uLightInfo[i].y) break;
    }
    vec4 R = uLightRect[li];
    vec4 I = uLightInfo[li];
    vec3 q = vec3(mix(R.x, R.z, rnd(s)), I.x, mix(R.y, R.w, rnd(s)));
    vec3 dv = q - p;
    float d2 = dot(dv, dv);
    float dist = sqrt(d2);
    vec3 wi = dv / dist;
    float c1 = dot(n, wi);
    float c2 = wi.y; // چراغ رو به پایین است
    if (c1 > 0.0 && c2 > 0.0 && d2 > 1e-6) {
      int h;
      traverse(p, wi, dist - 0.01, true, h);
      if (h < 0) E += uLightL[li] * (c1 * c2 * I.z / (d2 * I.w));
    }
  }
  return E;
}

vec3 irradiance(vec3 P, vec3 N, inout uint s) {
  vec3 o = P + N * EPS;
  vec3 E0 = directE(o, N, s);
  vec3 acc = vec3(0.0);
  vec3 thr = vec3(1.0);
  vec3 n = N;
  for (int b = 0; b < 8; b++) {
    if (b >= uBounces) break;
    vec3 d = cosineSample(n, s);
    int h;
    float t = traverse(o, d, 1e5, false, h);
    if (h < 0) {
      acc += thr * sky(d);
      break;
    }
    vec4 t0 = texelFetch(uTris, tc(h * 3), 0);
    vec3 v1 = texelFetch(uTris, tc(h * 3 + 1), 0).xyz;
    vec3 v2 = texelFetch(uTris, tc(h * 3 + 2), 0).xyz;
    vec3 fn = normalize(cross(v1 - t0.xyz, v2 - t0.xyz));
    if (dot(fn, d) > 0.0) fn = -fn; // دوطرفه
    int mid = int(t0.w + 0.5);
    vec3 alb = texelFetch(uMats, tc(mid * 2), 0).rgb;
    vec3 em = texelFetch(uMats, tc(mid * 2 + 1), 0).rgb;
    acc += thr * em;
    vec3 x = o + d * t + fn * EPS;
    acc += thr * alb * INV_PI * directE(x, fn, s);
    thr *= alb;
    if (max(thr.r, max(thr.g, thr.b)) < 0.01) break;
    o = x;
    n = fn;
  }
  vec3 ind = PI * acc;
  float lum = dot(ind, vec3(0.2126, 0.7152, 0.0722));
  if (lum > uClamp) ind *= uClamp / lum; // جرقه‌های تک (firefly)
  return E0 + ind;
}

void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 prev = texelFetch(uPrev, p, 0);
  vec4 P = texelFetch(uPos, p, 0);
  if (P.w < 0.5) {
    fragColor = prev;
    return;
  }
  vec3 N = normalize(texelFetch(uNrm, p, 0).xyz);
  uint s = uint(p.x) * 1973u + uint(p.y) * 9277u + uint(uFrame) * 26699u;
  s = s | 1u;
  pcg(s);
  pcg(s);
  vec3 E = irradiance(P.xyz, N, s);
  if (any(isnan(E)) || any(isinf(E))) {
    fragColor = prev;
    return;
  }
  float cnt = prev.w + 1.0;
  fragColor = vec4(prev.rgb + (E - prev.rgb) / cnt, cnt);
}
`;

// کپیِ ساده (برای برگرداندنِ یک تکه از تصویرِ موقت به تصویرِ اصلی)
export const COPY_FRAG = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D uSrc;
out vec4 fragColor;
void main() {
  fragColor = texelFetch(uSrc, ivec2(gl_FragCoord.xy), 0);
}
`;

// ---------------------------------------------------------------------------------------------------
// ۳) حذفِ نویز (à-trous): میانگینِ وزنیِ همسایه‌ها — فقط همسایه‌هایی که روی همان سطح‌اند (هم‌جهت و نزدیک در
//    فضا)، تا نورِ یک دیوار روی دیوارِ کناری یا گوشه‌ها پخش نشود.

export const DENOISE_FRAG = /* glsl */ `
${COMMON}
uniform sampler2D uSrc;
uniform sampler2D uPos;
uniform sampler2D uNrm;
uniform int uStep;
uniform float uTexelWorld;
uniform float uSigmaL;
out vec4 fragColor;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 c = texelFetch(uSrc, p, 0);
  vec4 P0 = texelFetch(uPos, p, 0);
  if (P0.w < 0.5) {
    fragColor = c;
    return;
  }
  vec3 n0 = texelFetch(uNrm, p, 0).xyz;
  float l0 = dot(c.rgb, vec3(0.2126, 0.7152, 0.0722));
  ivec2 size = textureSize(uSrc, 0);
  float k[3] = float[3](0.375, 0.25, 0.0625);
  vec3 sum = vec3(0.0);
  float wsum = 0.0;
  float sigmaP = float(uStep) * uTexelWorld * 2.5;
  for (int dy = -2; dy <= 2; dy++) {
    for (int dx = -2; dx <= 2; dx++) {
      ivec2 q = p + ivec2(dx, dy) * uStep;
      if (q.x < 0 || q.y < 0 || q.x >= size.x || q.y >= size.y) continue;
      vec4 Pq = texelFetch(uPos, q, 0);
      if (Pq.w < 0.5) continue;
      vec4 cq = texelFetch(uSrc, q, 0);
      vec3 nq = texelFetch(uNrm, q, 0).xyz;
      float wn = pow(max(0.0, dot(n0, nq)), 64.0);
      vec3 dp = Pq.xyz - P0.xyz;
      float wp = exp(-dot(dp, dp) / (sigmaP * sigmaP)) * exp(-abs(dot(dp, n0)) / (uTexelWorld * 0.5));
      float lq = dot(cq.rgb, vec3(0.2126, 0.7152, 0.0722));
      float wl = exp(-abs(lq - l0) / (uSigmaL * (l0 + 0.05)));
      float w = k[abs(dx)] * k[abs(dy)] * wn * wp * wl;
      sum += cq.rgb * w;
      wsum += w;
    }
  }
  fragColor = vec4(wsum > 0.0 ? sum / wsum : c.rgb, c.w);
}
`;

// ---------------------------------------------------------------------------------------------------
// ۴) «گسترش» برای نمایش: نقطه‌های خالیِ دورِ هر تکه با نزدیک‌ترین نقطه‌ی پرِ همان تکه پر می‌شوند؛ وگرنه کارت
//    گرافیک هنگامِ نمایش (نمونه‌برداریِ نرم) لبه‌ی تکه‌ها را با سیاهی مخلوط می‌کند (خطِ تیره روی درزها).

export const RESOLVE_FRAG = /* glsl */ `
${COMMON}
uniform sampler2D uSrc;
uniform sampler2D uPos;
out vec4 fragColor;
bool filled(ivec2 q, ivec2 size) {
  if (q.x < 0 || q.y < 0 || q.x >= size.x || q.y >= size.y) return false;
  return texelFetch(uPos, q, 0).w > 0.5 && texelFetch(uSrc, q, 0).w > 0.0;
}
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  ivec2 size = textureSize(uSrc, 0);
  if (filled(p, size)) {
    fragColor = vec4(texelFetch(uSrc, p, 0).rgb, 1.0);
    return;
  }
  for (int r = 1; r <= 3; r++) {
    vec3 sum = vec3(0.0);
    float n = 0.0;
    for (int dy = -r; dy <= r; dy++) {
      for (int dx = -r; dx <= r; dx++) {
        if (max(abs(dx), abs(dy)) != r) continue;
        ivec2 q = p + ivec2(dx, dy);
        if (filled(q, size)) {
          sum += texelFetch(uSrc, q, 0).rgb;
          n += 1.0;
        }
      }
    }
    if (n > 0.0) {
      fragColor = vec4(sum / n, 1.0);
      return;
    }
  }
  fragColor = vec4(0.0);
}
`;
